import React, { useMemo } from "react";
import { Button, Card, Col, Row } from "antd";
import ReactECharts from "echarts-for-react";
import {
  PNL_COLORS,
  PnlMonthColumn,
  fmtINR,
  fmtINRCompact,
  fmtMonth,
  num,
} from "./pnlShared";

// Charts for the Monthly Budget P&L (pattern: MaterialCostCharts.tsx —
// echarts-for-react, antd Cards in a gutter Row).
// 1. Monthly Cash Profile — stacked outflow bars (3 pillars) + inflow line.
// 2. Cost Mix — donut of the three computed pillars.
// 3. Cumulative Margin — running Σ(inflow − outflow) line with a zero mark.

const PnlCharts: React.FC<{
  months: PnlMonthColumn[];
  onViewDetails?: () => void;
}> = ({ months, onViewDetails }) => {
  const labels = useMemo(() => months.map((m) => fmtMonth(m.month)), [months]);

  const cashProfileOption = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        valueFormatter: (value: number) => fmtINRCompact(value),
      },
      legend: { top: 0 },
      grid: { left: 70, right: 24, top: 40, bottom: 28 },
      xAxis: { type: "category", data: labels },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (value: number) => fmtINRCompact(value) },
      },
      series: [
        {
          name: "Material",
          type: "bar",
          stack: "outflow",
          color: PNL_COLORS.material,
          data: months.map((m) => num(m.materialAmount)),
        },
        {
          name: "Manpower",
          type: "bar",
          stack: "outflow",
          color: PNL_COLORS.manpower,
          data: months.map((m) => num(m.subconPrwAmount)),
        },
        {
          name: "Machinery",
          type: "bar",
          stack: "outflow",
          color: PNL_COLORS.machinery,
          data: months.map((m) => num(m.machineryAmount)),
        },
        {
          name: "Inflow",
          type: "line",
          z: 10,
          color: PNL_COLORS.inflow,
          symbol: "circle",
          symbolSize: 7,
          lineStyle: { width: 2.5 },
          data: months.map((m) => num(m.totalInflow)),
        },
      ],
    }),
    [months, labels]
  );

  const costMixOption = useMemo(() => {
    const material = months.reduce((total, m) => total + num(m.materialAmount), 0);
    const manpower = months.reduce((total, m) => total + num(m.subconPrwAmount), 0);
    const machinery = months.reduce((total, m) => total + num(m.machineryAmount), 0);
    const total = material + manpower + machinery;
    return {
      tooltip: {
        trigger: "item",
        formatter: (params: any) =>
          `${params.name}: ${fmtINR(params.value)} (${params.percent}%)`,
      },
      legend: { bottom: 0 },
      series: [
        {
          type: "pie",
          radius: ["55%", "80%"],
          center: ["50%", "45%"],
          avoidLabelOverlap: true,
          label: { show: false },
          data: [
            { name: "Material", value: material, itemStyle: { color: PNL_COLORS.material } },
            { name: "Manpower", value: manpower, itemStyle: { color: PNL_COLORS.manpower } },
            { name: "Machinery", value: machinery, itemStyle: { color: PNL_COLORS.machinery } },
          ],
        },
      ],
      graphic: {
        type: "text",
        left: "center",
        top: "41%",
        style: {
          text: `${fmtINRCompact(total)}\nOutflow`,
          textAlign: "center",
          fill: "#022c57",
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 20,
        },
      },
    };
  }, [months]);

  const cumulativeMarginOption = useMemo(() => {
    let running = 0;
    const cumulative = months.map((m) => {
      running += num(m.margin);
      return Math.round(running * 100) / 100;
    });
    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: any[]) => {
          const point = params?.[0];
          if (!point) return "";
          const monthMargin = num(months[point.dataIndex]?.margin);
          return `${point.axisValue}<br/>Month margin: ${fmtINRCompact(monthMargin)}<br/>Cumulative: ${fmtINRCompact(point.value)}`;
        },
      },
      grid: { left: 70, right: 24, top: 24, bottom: 28 },
      xAxis: { type: "category", data: labels },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (value: number) => fmtINRCompact(value) },
      },
      series: [
        {
          name: "Cumulative Margin",
          type: "line",
          color: PNL_COLORS.margin,
          symbol: "circle",
          symbolSize: 6,
          areaStyle: { opacity: 0.12 },
          markLine: {
            symbol: "none",
            lineStyle: { color: PNL_COLORS.negative, type: "dashed" },
            label: { formatter: "0" },
            data: [{ yAxis: 0 }],
          },
          data: cumulative,
        },
      ],
    };
  }, [months, labels]);

  if (!months.length) return null;

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} lg={16}>
          <Card
            size="small"
            title="Monthly Cash Profile"
            extra={
              onViewDetails ? (
                <Button type="link" size="small" onClick={onViewDetails}>
                  View Details
                </Button>
              ) : null
            }
            bodyStyle={{ padding: 8 }}
          >
            <ReactECharts option={cashProfileOption} style={{ height: 320 }} notMerge />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="Cost Mix" bodyStyle={{ padding: 8 }}>
            <ReactECharts option={costMixOption} style={{ height: 320 }} notMerge />
          </Card>
        </Col>
      </Row>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card size="small" title="Cumulative Margin" bodyStyle={{ padding: 8 }}>
            <ReactECharts option={cumulativeMarginOption} style={{ height: 240 }} notMerge />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PnlCharts;
